import React, {useEffect, useState} from 'react';
import type {MenuProps} from 'antd';
import {App, Button, Card, Col, Dropdown, Empty, Modal, Pagination, Row, Spin, Tag, Tooltip, Typography} from 'antd';
import {
    ClockCircleOutlined,
    DeleteOutlined,
    GlobalOutlined,
    StarFilled,
    StarOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {News, NewsAnalysis, newsService, NewsTranslation} from '../../services/newsService';

const {Title, Paragraph, Text} = Typography;

interface NewsSourceNewsModalProps {
    visible: boolean;
    sourceId: number | null;
    sourceName: string;
    onCancel: () => void;
}

/**
 * 获取情感配置
 */
const getSentimentConfig = (sentiment?: string) => {
    switch (sentiment) {
        case 'POSITIVE':
            return {label: '利好', color: 'var(--pt-bull)', bg: 'var(--pt-bull-dim)'};
        case 'NEGATIVE':
            return {label: '利空', color: 'var(--pt-bear)', bg: 'var(--pt-bear-dim)'};
        case 'NEUTRAL':
            return {label: '中性', color: 'var(--pt-blue)', bg: 'var(--pt-blue-dim)'};
        default:
            return {label: '未分析', color: '#d9d9d9', bg: '#fafafa'};
    }
};

/**
 * 解析JSON数组
 */
const parseJsonArray = (jsonStr?: string): string[] => {
    if (!jsonStr) return [];
    try {
        return JSON.parse(jsonStr);
    } catch {
        return [];
    }
};

/**
 * 获取交易机会标签
 */
const getOpportunityLabel = (opportunity?: string) => {
    switch (opportunity) {
        case 'LONG':
            return '做多';
        case 'SHORT':
            return '做空';
        case 'NEUTRAL':
            return '中性';
        case 'WAIT':
            return '观望';
        default:
            return opportunity || '未分析';
    }
};

/**
 * 获取交易机会背景色
 */
const getOpportunityBg = (opportunity?: string) => {
    switch (opportunity) {
        case 'LONG':
            return 'var(--pt-bull-dim)';
        case 'SHORT':
            return 'var(--pt-bear-dim)';
        case 'NEUTRAL':
            return 'var(--pt-blue-dim)';
        case 'WAIT':
            return 'rgb(80, 80, 80)';
        default:
            return '#fafafa';
    }
};

/**
 * 获取交易机会文字色
 */
const getOpportunityColor = (opportunity?: string) => {
    switch (opportunity) {
        case 'LONG':
            return 'var(--pt-bull)';
        case 'SHORT':
            return 'var(--pt-bear)';
        case 'NEUTRAL':
            return '#8c8c8c';
        case 'WAIT':
            return 'var(--pt-amber)';
        default:
            return '#d9d9d9';
    }
};

/**
 * RSS源新闻浏览弹窗组件
 */
const NewsSourceNewsModal: React.FC<NewsSourceNewsModalProps> = ({
                                                                     visible,
                                                                     sourceId,
                                                                     sourceName,
                                                                     onCancel,
                                                                 }) => {
    const {message: messageApi} = App.useApp();
    const [loading, setLoading] = useState(false);
    const [newsList, setNewsList] = useState<News[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [translations, setTranslations] = useState<Map<number, NewsTranslation>>(new Map());
    const [translatingIds, setTranslatingIds] = useState<Set<number>>(new Set());
    const [analyzingId, setAnalyzingId] = useState<number | null>(null);
    const [analysisResult, setAnalysisResult] = useState<NewsAnalysis | null>(null);
    const [analysisModalVisible, setAnalysisModalVisible] = useState(false);
    const pageSize = 20;

    useEffect(() => {
        if (visible && sourceId) {
            loadNews();
        }
    }, [visible, sourceId, page]);

    const loadNews = async () => {
        if (!sourceId) return;
        setLoading(true);
        try {
            const result = await newsService.getNewsBySource(sourceId, page, pageSize);
            setNewsList(result.content || []);
            setTotal(result.totalElements || 0);
        } catch (error) {
            console.error('加载新闻失败:', error);
            messageApi.error('加载新闻失败');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage - 1); // Ant Design页码从1开始
    };

    /**
     * 处理翻译按钮点击
     */
    const handleTranslate = async (news: News) => {
        // 检查是否已有翻译
        if (translations.has(news.id)) {
            messageApi.info('该新闻已有翻译');
            return;
        }

        // 检查是否正在翻译
        if (translatingIds.has(news.id)) {
            return;
        }

        setTranslatingIds((prev) => new Set(prev).add(news.id));
        try {
            const translation = await newsService.translateNews(news.id, 'zh');
            setTranslations((prev) => {
                const newMap = new Map(prev);
                newMap.set(news.id, translation);
                return newMap;
            });
            messageApi.success('翻译完成');
        } catch (error) {
            console.error('翻译失败:', error);
            messageApi.error('翻译失败');
        } finally {
            setTranslatingIds((prev) => {
                const newSet = new Set(prev);
                newSet.delete(news.id);
                return newSet;
            });
        }
    };

    /**
     * 获取显示的标题和摘要（优先显示翻译）
     */
    const getDisplayContent = (news: News) => {
        // 先检查本地翻译状态（新翻译的）
        const localTranslation = translations.get(news.id);
        if (localTranslation) {
            return {
                title: localTranslation.titleTranslated,
                summary: localTranslation.summaryTranslated || news.summary,
                isTranslated: true,
            };
        }
        // 再检查后端返回的翻译
        if (news.hasTranslation && news.titleTranslated) {
            return {
                title: news.titleTranslated,
                summary: news.summaryTranslated || news.summary,
                isTranslated: true,
            };
        }
        return {
            title: news.title,
            summary: news.summary,
            isTranslated: false,
        };
    };

    const handleCardAction = (action: string, news: News) => {
        messageApi.info(`${action}功能开发中`);
    };

    /**
     * 处理分析按钮点击
     */
    const handleAnalyze = async (news: News) => {
        if (analyzingId) return; // 防止重复点击

        setAnalyzingId(news.id);
        try {
            const result = await newsService.analyzeNews(news.id);
            setAnalysisResult(result);
            setAnalysisModalVisible(true);
        } catch (error) {
            console.error('分析失败:', error);
            messageApi.error('分析失败');
        } finally {
            setAnalyzingId(null);
        }
    };

    /**
     * 重要性配置
     */
    const getImportanceConfig = (importance?: number) => {
        switch (importance) {
            case 1:
                return {label: '一般', color: '#8c8c8c', icon: <StarOutlined/>};
            case 2:
                return {
                    label: '重要',
                    color: 'var(--pt-amber)',
                    icon: <StarFilled style={{color: 'var(--pt-amber)'}}/>
                };
            case 3:
                return {label: '无关', color: '#d9d9d9', icon: <StarOutlined style={{color: '#d9d9d9'}}/>};
            default:
                return {label: '一般', color: '#8c8c8c', icon: <StarOutlined/>};
        }
    };

    /**
     * 处理重要性更新
     */
    const handleImportanceChange = async (news: News, importance: number) => {
        try {
            await newsService.updateImportance(news.id, importance);
            // 更新本地状态
            setNewsList(prev => prev.map(item =>
                item.id === news.id ? {...item, importance} : item
            ));
            messageApi.success('重要性已更新');
        } catch (error) {
            console.error('更新重要性失败:', error);
            messageApi.error('更新失败');
        }
    };

    /**
     * 获取重要性下拉菜单
     */
    const getImportanceMenu = (news: News): MenuProps['items'] => [
        {
            key: '1',
            label: '一般',
            icon: <StarOutlined/>,
            onClick: () => handleImportanceChange(news, 1),
        },
        {
            key: '2',
            label: '重要',
            icon: <StarFilled style={{color: 'var(--pt-amber)'}}/>,
            onClick: () => handleImportanceChange(news, 2),
        },
        {
            key: '3',
            label: '无关',
            icon: <StarOutlined style={{color: '#d9d9d9'}}/>,
            onClick: () => handleImportanceChange(news, 3),
        },
    ];

    const handleCardClick = (news: News) => {
        window.open(news.originalUrl, '_blank');
    };

    /**
     * 处理删除新闻
     */
    const handleDelete = async (news: News) => {
        try {
            await newsService.deleteNews(news.id);
            // 从列表中移除
            setNewsList(prev => prev.filter(item => item.id !== news.id));
            setTotal(prev => prev - 1);
            messageApi.success('删除成功');
        } catch (error) {
            console.error('删除失败:', error);
            messageApi.error('删除失败');
        }
    };

    return (
        <Modal
            title={`${sourceName} - 新闻列表 (共 ${total} 条)`}
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={1000}
            styles={{body: {maxHeight: '70vh', overflowY: 'auto'}}}
        >
            <Spin spinning={loading}>
                {newsList.length === 0 && !loading ? (
                    <Empty description="暂无新闻" style={{padding: '40px 0'}}/>
                ) : (
                    <>
                        <Row gutter={[16, 16]} style={{marginBottom: 16}}>
                            {newsList.map((news) => {
                                const sentimentConfig = getSentimentConfig(news.sentiment);
                                const displayContent = getDisplayContent(news);
                                const isTranslating = translatingIds.has(news.id);
                                const hasTranslation = displayContent.isTranslated;
                                return (
                                    <Col key={news.id} xs={24} sm={12} md={8}>
                                        <Card
                                            hoverable
                                            style={{
                                                height: '100%',
                                                borderRadius: 8,
                                                transition: 'all 0.2s',
                                                minHeight: 200,
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}
                                            styles={{
                                                body: {
                                                    padding: 16,
                                                    flex: 1,
                                                    display: 'flex',
                                                    flexDirection: 'column'
                                                }
                                            }}
                                            onClick={() => handleCardClick(news)}
                                        >
                                            {/* 来源和时间 */}
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    marginBottom: 8,
                                                    fontSize: 12,
                                                    color: '#8c8c8c',
                                                }}
                                            >
                        <span>
                          {news.sourceName || '未知来源'}
                            {displayContent.isTranslated && (
                                <Tag style={{
                                    marginLeft: 4,
                                    fontSize: 9,
                                    transform: 'scale(0.5)',
                                    transformOrigin: 'left center'
                                }} color="blue">翻译</Tag>
                            )}
                        </span>
                                                <span style={{display: 'flex', alignItems: 'center', gap: 4}}>
                          <ClockCircleOutlined/>
                                                    {dayjs(news.publishedAt).format('MM-DD HH:mm')}
                        </span>
                                            </div>

                                            {/* 标题 - 2行 */}
                                            <Title
                                                level={5}
                                                ellipsis={{rows: 2}}
                                                style={{marginBottom: 8, fontSize: 14, minHeight: 44}}
                                            >
                                                {displayContent.title}
                                            </Title>

                                            {/* 摘要 - 3行 */}
                                            {displayContent.summary && (
                                                <Paragraph
                                                    ellipsis={{rows: 3}}
                                                    style={{
                                                        fontSize: 12,
                                                        color: '#8c8c8c',
                                                        marginBottom: 8,
                                                        minHeight: 54
                                                    }}
                                                >
                                                    {displayContent.summary}
                                                </Paragraph>
                                            )}

                                            {/* 情感标签 - 固定在底部 */}
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    paddingTop: 8,
                                                    borderTop: '1px solid #f0f0f0',
                                                    marginTop: 'auto',
                                                }}
                                            >
                                                <Tag
                                                    style={{
                                                        margin: 0,
                                                        background: sentimentConfig.bg,
                                                        color: sentimentConfig.color,
                                                        border: 'none',
                                                        fontSize: 11,
                                                    }}
                                                >
                                                    {sentimentConfig.label}
                                                </Tag>
                                                <div style={{display: 'flex', gap: 2}}>
                                                    {parseJsonArray(news.marketTags)
                                                        .slice(0, 2)
                                                        .map((tag) => (
                                                            <Tag key={tag} style={{margin: 0, fontSize: 11}}>
                                                                {tag}
                                                            </Tag>
                                                        ))}
                                                </div>
                                            </div>

                                            {/* 管理按钮banner - 固定在底部 */}
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-around',
                                                    paddingTop: 8,
                                                    borderTop: '1px solid #f0f0f0',
                                                    marginTop: 'auto',
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Tooltip title={displayContent.isTranslated ? '已翻译' : '翻译'}>
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<GlobalOutlined/>}
                                                        loading={isTranslating}
                                                        style={displayContent.isTranslated ? {color: 'var(--pt-bull)'} : undefined}
                                                        onClick={() => handleTranslate(news)}
                                                    />
                                                </Tooltip>
                                                <Tooltip title={news.analyzedAt ? '重新分析' : '分析'}>
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={
                                                            news.analyzedAt
                                                                ?
                                                                <ThunderboltOutlined style={{color: 'var(--pt-bull)'}}/>
                                                                : <ThunderboltOutlined style={{color: '#722ed1'}}/>
                                                        }
                                                        onClick={() => handleAnalyze(news)}
                                                        loading={analyzingId === news.id}
                                                    />
                                                </Tooltip>
                                                <Dropdown menu={{items: getImportanceMenu(news)}} trigger={['click']}>
                                                    <Tooltip
                                                        title={`重要性: ${getImportanceConfig(news.importance).label}`}>
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            icon={getImportanceConfig(news.importance).icon}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </Tooltip>
                                                </Dropdown>
                                                <Tooltip title="删除">
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        danger
                                                        icon={<DeleteOutlined/>}
                                                        onClick={() => handleDelete(news)}
                                                    />
                                                </Tooltip>
                                            </div>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>

                        {/* 分页 */}
                        {total > pageSize && (
                            <div style={{display: 'flex', justifyContent: 'center', marginTop: 16}}>
                                <Pagination
                                    current={page + 1}
                                    pageSize={pageSize}
                                    total={total}
                                    onChange={handlePageChange}
                                    showSizeChanger={false}
                                    showTotal={(total) => `共 ${total} 条`}
                                />
                            </div>
                        )}
                    </>
                )}
            </Spin>

            {/* 分析结果弹窗 */}
            <Modal
                title="新闻分析结果"
                open={analysisModalVisible}
                onCancel={() => setAnalysisModalVisible(false)}
                footer={null}
                width={600}
            >
                {analysisResult && (
                    <div style={{padding: '8px 0'}}>
                        {/* 交易机会标签 */}
                        <div style={{marginBottom: 16}}>
                            <Tag
                                style={{
                                    fontSize: 14,
                                    padding: '4px 12px',
                                    background: getOpportunityBg(analysisResult.tradingOpportunity),
                                    color: getOpportunityColor(analysisResult.tradingOpportunity),
                                    border: 'none'
                                }}
                            >
                                {getOpportunityLabel(analysisResult.tradingOpportunity)}
                            </Tag>
                            <span style={{marginLeft: 12, color: '#8c8c8c'}}>
                机会评分: <strong
                                style={{color: 'var(--pt-amber)'}}>{analysisResult.opportunityScore?.toFixed(2) || '-'}</strong>
                                {' | '}
                                置信度: <strong
                                style={{color: '#722ed1'}}>{analysisResult.confidence ? `${(analysisResult.confidence * 100).toFixed(1)}%` : '-'}</strong>
              </span>
                        </div>

                        {/* 分析摘要 */}
                        {analysisResult.analysisSummary && (
                            <div style={{marginBottom: 16}}>
                                <Text type="secondary" style={{fontSize: 12}}>分析摘要</Text>
                                <Paragraph style={{margin: '4px 0 0 0', fontSize: 14}}>
                                    {analysisResult.analysisSummary}
                                </Paragraph>
                            </div>
                        )}

                        {/* 行动建议 */}
                        {analysisResult.actionSuggestion && (
                            <div style={{marginBottom: 16}}>
                                <Text type="secondary" style={{fontSize: 12}}>行动建议</Text>
                                <Paragraph style={{margin: '4px 0 0 0', fontSize: 14, color: 'var(--pt-bull)'}}>
                                    {analysisResult.actionSuggestion}
                                </Paragraph>
                            </div>
                        )}

                        {/* 风险提示 */}
                        {analysisResult.riskWarning && (
                            <div style={{marginBottom: 16}}>
                                <Text type="secondary" style={{fontSize: 12}}>风险提示</Text>
                                <Paragraph style={{margin: '4px 0 0 0', fontSize: 14, color: 'var(--pt-bear)'}}>
                                    {analysisResult.riskWarning}
                                </Paragraph>
                            </div>
                        )}

                        {/* 关键因素 */}
                        {analysisResult.keyFactors && (
                            <div style={{marginBottom: 16}}>
                                <Text type="secondary" style={{fontSize: 12}}>关键因素</Text>
                                <Paragraph style={{margin: '4px 0 0 0', fontSize: 14}}>
                                    {analysisResult.keyFactors}
                                </Paragraph>
                            </div>
                        )}

                        {/* 影响的市场/币种 */}
                        {(analysisResult.affectedMarkets || analysisResult.affectedSymbols) && (
                            <div style={{marginBottom: 16}}>
                                <Text type="secondary" style={{fontSize: 12}}>影响范围</Text>
                                <div style={{marginTop: 4}}>
                                    {analysisResult.affectedMarkets && (
                                        <Tag color="blue"
                                             style={{marginRight: 4}}>{analysisResult.affectedMarkets}</Tag>
                                    )}
                                    {analysisResult.affectedSymbols && (
                                        <Tag color="green">{analysisResult.affectedSymbols}</Tag>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* 分析信息 */}
                        <div style={{borderTop: '1px solid #f0f0f0', paddingTop: 12, marginTop: 16}}>
                            <Text type="secondary" style={{fontSize: 11}}>
                                {analysisResult.modelUsed && `模型: ${analysisResult.modelUsed}`}
                                {analysisResult.tokensUsed && ` | Tokens: ${analysisResult.tokensUsed}`}
                                {analysisResult.analysisDuration && ` | 耗时: ${(analysisResult.analysisDuration / 1000).toFixed(1)}s`}
                            </Text>
                        </div>
                    </div>
                )}
            </Modal>
        </Modal>
    );
};

export default NewsSourceNewsModal;
